/**
 * Reading the route and the dose unit off an RxNorm clinical-drug name.
 *
 * The medication search returns SCD/SBD concepts, whose display follows a fixed grammar —
 * ingredient, strength, dose form — so the dose form is readable from the string itself:
 *
 *   aspirin 81 MG Oral Tablet            -> Oral, Tablet
 *   mometasone 0.05 MG/ACTUAT Nasal Spray -> Nasal, Spray
 *   fentanyl 25 MCG/HR Transdermal System -> Transdermal, Patch
 *
 * Deliberately partial. A form that names only one of the two fills only that one, and a
 * form that names neither fills neither — a wrong prefill on a field that locks at save
 * costs more than a blank one. The strength is never read: `81 MG` is how much drug is in
 * one tablet, not how many tablets to give, and the dose field means the latter.
 */

// The app's own route list is the target vocabulary; anything outside it stays blank.
// Ordered: an anatomic word wins over `inhal`, so "Nasal Inhalant" reads as Nasal.
const ROUTES = [
    [/\bsublingual\b/i, 'Sublingual'],
    [/\bbuccal\b/i, 'Buccal'],
    [/\bophthalmic\b/i, 'Ophthalmic'],
    [/\botic\b|\bauricular\b/i, 'Otic'],
    [/\bnasal\b/i, 'Nasal'],
    [/\btransdermal\b/i, 'Transdermal'],
    [/\btopical\b/i, 'Topical'],
    [/\brectal\b/i, 'Rectal'],
    [/\bvaginal\b/i, 'Vaginal'],
    [/\boral\b|\bchewable\b|\blozenge\b|\btroche\b/i, 'Oral'],
    [/\binhal/i, 'Inhalation'],
];

// A countable form names its own unit. Ordered most specific first: a transdermal system is
// a patch before it is anything else, and a metered dose nasal spray is a spray, not a
// solution.
const COUNTABLE = [
    [/\bsuppository\b/i, 'Suppository'],
    [/\btransdermal system\b|\bpatch\b/i, 'Patch'],
    [/\btablet\b/i, 'Tablet'],
    [/\bcapsule\b/i, 'Capsule'],
    [/\binhaler\b/i, 'Puff'],
    [/\bspray\b/i, 'Spray'],
    [/\bdrops?\b/i, 'Drop'],
];

const LIQUID = /\bsolution\b|\bsuspension\b|\belixir\b|\bsyrup\b|\bconcentrate\b|\btincture\b|\bsyringe\b|\benema\b/i;

// A liquid is measured by where it goes: an eye takes drops, a mouth takes millilitres, and
// a cream has no countable unit at all.
const LIQUID_UNITS = { Ophthalmic: 'Drop', Otic: 'Drop', Oral: 'mL', Nasal: 'mL', Inhalation: 'mL', Rectal: 'mL' };

// Route is genuinely ambiguous — IV, IM and subcutaneous are all live options and the form
// does not say which — but the measure is not.
const INJECTABLE = /\binjectab|\binjection\b|\bsyringe\b|\bauto-?injector\b/i;

const routeOf = (name) => ROUTES.find(([pattern]) => pattern.test(name))?.[1];

const unitOf = (name, route) => {
    const countable = COUNTABLE.find(([pattern]) => pattern.test(name))?.[1];
    if (countable) return countable;
    if (!LIQUID.test(name)) return undefined;
    if (INJECTABLE.test(name)) return 'mL';
    return LIQUID_UNITS[route];
};

/**
 * The route and dose unit an RxNorm display names, as values from the medication dialog's
 * own option lists. Either may be undefined.
 *
 * @param {string} display - an SCD/SBD concept display, e.g. 'aspirin 81 MG Oral Tablet'
 * @returns {{route: string|undefined, unit: string|undefined}}
 */
export const doseFormOf = (display) => {
    // A branded name carries the brand in trailing brackets, which names no form.
    const name = String(display || '').replace(/\[[^\]]*\]/g, '');
    if (!name.trim()) return { route: undefined, unit: undefined };

    const route = routeOf(name);
    return { route, unit: unitOf(name, route) };
};
